import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Heading,
  IconButton,
  Input,
  InputGroup,
  Spinner,
  Text,
  VStack,
  Field,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { FiCheck, FiCircle, FiEye, FiEyeOff } from 'react-icons/fi'
import { authApi } from '../lib/api'
import { AUTH_USER_KEY } from '../lib/authRedirect'
import { isNativeApp } from '../lib/native/platform'
import { setCredential } from '../lib/native/credentialStore'
import type { InvitePreview } from '../types/invites'

const MIN_PASSWORD_LENGTH = 12

function deviceLabel(): string {
  return (navigator.userAgent || 'device').slice(0, 60)
}

interface AcceptInviteForm {
  firstName: string
  lastName: string
  password: string
  confirmPassword: string
}

function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()

  const [preview, setPreview] = useState<InvitePreview | null>(null)
  const [previewLoading, setPreviewLoading] = useState(true)
  const [previewError, setPreviewError] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AcceptInviteForm>()

  const passwordValue = watch('password') || ''
  const confirmPasswordValue = watch('confirmPassword') || ''
  const passwordOk = passwordValue.length >= MIN_PASSWORD_LENGTH
  const matchOk = confirmPasswordValue.length > 0 && confirmPasswordValue === passwordValue

  useEffect(() => {
    if (!token) {
      setPreviewError(true)
      setPreviewLoading(false)
      return
    }

    let cancelled = false
    setPreviewLoading(true)
    authApi
      .invitePreview(token)
      .then((resp) => {
        if (!cancelled) setPreview(resp.data)
      })
      .catch(() => {
        if (!cancelled) setPreviewError(true)
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [token])

  const onSubmit = async (data: AcceptInviteForm) => {
    if (!token) return
    setSubmitError(null)
    setIsSubmitting(true)

    try {
      const native = isNativeApp()
      const response = await authApi.acceptInvite({
        token,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        ...(native ? { deviceLabel: deviceLabel() } : {}),
      })

      // [Native B1, NB1-1] The od_ token goes ONLY to OS secure storage. If it cannot be stored
      // (no instance selected), the account still exists — send them to sign in normally.
      if (native) {
        const sessionToken = response.data.sessionToken
        try {
          if (!sessionToken) throw new Error('No session token')
          await setCredential('organizer', sessionToken)
        } catch {
          navigate('/login', { replace: true })
          return
        }
      }

      // Store user info (web: token is in HttpOnly cookie set by server) — auto-login.
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(response.data.user))
      navigate('/dashboard')
    } catch (err: any) {
      setSubmitError(
        err.response?.data?.message || 'Failed to accept invite. Please try again.',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  if (previewLoading) {
    return (
      <Flex minH="100vh" align="center" justify="center">
        <Spinner size="lg" color="accent" />
      </Flex>
    )
  }

  if (previewError || !preview) {
    return (
      <Container maxW="container.sm" py={16}>
        <Alert.Root status="error" borderRadius="md">
          <Alert.Indicator />
          This invitation is invalid or has expired.
        </Alert.Root>
      </Container>
    );
  }

  return (
    <Container maxW="container.sm" py={16}>
      <VStack gap={8} align="stretch">
        <Box textAlign="center">
          <Heading size="xl" mb={2}>
            Set up your account
          </Heading>
          <Text color="gray.600">
            {preview.email} · {preview.role}
            {preview.committeeLabel ? ` · ${preview.committeeLabel}` : ''}
          </Text>
        </Box>

        <Box bg="surface" p={8} borderRadius="lg" shadow="md">
          <form onSubmit={handleSubmit(onSubmit)}>
            <VStack gap={4}>
              {!!submitError && (
                <Alert.Root status="error" borderRadius="md">
                  <Alert.Indicator />
                  {submitError}
                </Alert.Root>
              )}

              <Field.Root required invalid={!!errors.firstName}>
                <Field.Label>First name</Field.Label>
                <Input
                  data-testid="accept-invite-first-name"
                  {...register('firstName', { required: true })}
                />
              </Field.Root>

              <Field.Root required invalid={!!errors.lastName}>
                <Field.Label>Last name</Field.Label>
                <Input
                  data-testid="accept-invite-last-name"
                  {...register('lastName', { required: true })}
                />
              </Field.Root>

              <Field.Root required invalid={!!errors.password}>
                <Field.Label>Password</Field.Label>
                <InputGroup
                  endElement={
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </IconButton>
                  }
                >
                  <Input
                    data-testid="accept-invite-password"
                    type={showPassword ? 'text' : 'password'}
                    {...register('password', { required: true, minLength: MIN_PASSWORD_LENGTH })}
                  />
                </InputGroup>
                <Field.ErrorText>Password must be at least 12 characters.</Field.ErrorText>
              </Field.Root>

              <HStack gap={2} fontSize="sm" color={passwordOk ? 'success.fg' : 'fg.muted'}>
                {passwordOk ? <FiCheck /> : <FiCircle />}
                <Text>At least 12 characters</Text>
              </HStack>

              <Field.Root required invalid={!!errors.confirmPassword}>
                <Field.Label>Confirm password</Field.Label>
                <Input
                  data-testid="accept-invite-confirm-password"
                  type={showPassword ? 'text' : 'password'}
                  {...register('confirmPassword', {
                    required: true,
                    validate: (value) => value === watch('password') || 'Passwords do not match.',
                  })}
                />
                <Field.ErrorText>{errors.confirmPassword?.message}</Field.ErrorText>
              </Field.Root>

              {confirmPasswordValue.length > 0 && (
                <HStack gap={2} fontSize="sm" color={matchOk ? 'success.fg' : 'fg.muted'}>
                  {matchOk ? <FiCheck /> : <FiCircle />}
                  <Text>Passwords match</Text>
                </HStack>
              )}

              <Button
                type="submit"
                colorPalette="brand"
                width="full"
                mt={4}
                loading={isSubmitting}
                disabled={!(passwordOk && matchOk)}
              >
                Set password &amp; sign in
              </Button>
            </VStack>
          </form>
        </Box>
      </VStack>
    </Container>
  );
}

export default AcceptInvitePage
export { AcceptInvitePage }
